import React, { useCallback, useRef, useState } from 'react';

import { cn } from '../../lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  /** Agent colour hex for the send button */
  agentColour: string;
  /** Agent button variant for styling */
  agentButtonVariant: string;
  className?: string;
}

const CHARACTER_HINT = 2000;

/**
 * Chat input area with auto-growing textarea and send button.
 * Send on Enter (Shift+Enter for newline). Disabled while streaming.
 */
export function ChatInput({
  onSend,
  disabled,
  agentColour,
  className,
}: ChatInputProps): React.JSX.Element {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    // Auto-grow textarea
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const charCount = value.length;
  const isOverHint = charCount > CHARACTER_HINT;

  return (
    <div className={cn('border-t border-border bg-surface-raised px-4 py-3', className)}>
      <div className="flex items-end gap-3">
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Type your message…"
            rows={1}
            className={cn(
              'w-full resize-none rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-primary',
              'placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            style={{ maxHeight: '200px' }}
            aria-label="Message input"
          />
          {/* Character hint */}
          {charCount > CHARACTER_HINT * 0.8 && (
            <span
              className={cn(
                'absolute bottom-1 right-3 text-xs',
                isOverHint ? 'text-red-500' : 'text-text-muted',
              )}
              aria-live="polite"
            >
              {charCount}/{CHARACTER_HINT}
            </span>
          )}
        </div>

        {/* Send button */}
        <button
          type="button"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
          style={{ backgroundColor: disabled ? undefined : agentColour }}
          aria-label="Send message"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5"
          >
            <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25H10.5a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95l14.095-5.61a.75.75 0 000-1.392L3.105 2.289z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
